import { mergeDeep } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/mergeDeep'

// Two legend rows plus the pager: a row is the 13px line height + 1px item margin, floored by the
// swatch to ~16px, and the pager adds ~18px. 50 left the second row a pixel short and clipped it.
// Still short of a third row (~66px), so a longer legend paginates rather than eating chart height.
// The legacy chart models carry the same cap and must move with it.
const LEGEND_MAX_HEIGHT = 56
const FONT_FAMILY = 'Ubuntu, Calibri, Segoe, Helvetica, Arial'

// What the legend gives up either side: the chart's own left spacing plus the legend's inset on one
// side, and on the other the view/menu controls the header floats over the same band. A fixed legend
// width broke the line early on a wide chart and left the right third of the row empty.
const LEGEND_LEFT_INSET = 30
const LEGEND_CONTROLS_RESERVE = 130

// Room the names may run along before they turn onto the next line. Narrow charts keep a floor so a
// long answer still gets a line of its own rather than one word per row.
export const legendWidthFor = chartWidth => Math.max(240, chartWidth - LEGEND_LEFT_INSET - LEGEND_CONTROLS_RESERVE)

export const calcBaseConfig = overrides => {
  const base = {
    accessibility: {
      enabled: false
    },
    chart: {
      // Dormant while the builders pin their own `chart.margin`: a side resolves as
      // pick(margin, spacing), so the margin wins wherever one is set.
      spacingLeft: 20,
      spacingRight: 20
    },
    credits: {
      enabled: false
    },
    exporting: {
      enabled: false
    },
    legend: {
      // Starts where the scale's figures start, so the names above the chart and the numbers down its
      // side share one left edge instead of the legend floating centred over them.
      align: 'left',
      // Names follow one another along the line and turn onto the next when they run out of room, the
      // way a sentence does. Left on, Highcharts instead rules the legend into columns as wide as its
      // longest name, so one long answer opens a gap after every short one.
      alignColumns: false,
      borderWidth: 0,
      itemHiddenStyle: {
        color: '#cccccc',
        fontWeight: '300',
        textDecoration: 'none'
      },
      itemMarginTop: 1,
      itemStyle: {
        lineHeight: '13px',
        fontFamily: FONT_FAMILY,
        fontSize: '12px'
      },
      layout: 'horizontal',
      margin: 10,
      maxHeight: LEGEND_MAX_HEIGHT,
      navigation: {
        arrowSize: 10,
        style: {
          fontSize: 10
        }
      },
      padding: 0,
      shadow: false,
      verticalAlign: 'top',
      width: 550,
      // clears the chart's own left spacing and lands the first swatch on the scale figures' left edge
      x: 20,
      y: 10
    },
    plotOptions: {
      series: {
        dataLabels: {
          allowOverlap: false,
          color: '#000000',
          style: {
            fontFamily: FONT_FAMILY,
            fontSize: '12px',
            fontWeight: 400,
            lineHeight: '11px',
            textOverflow: 'ellipsis',
            wordBreak: 'break-word'
          }
        },
        stickyTracking: false
      }
    },
    series: [],
    title: {
      text: ''
    },
    tooltip: {
      // Object form anchors the gradient to the tooltip box (not absolute SVG coords) so the fill
      // is position-independent — the old `[0, 0, 0, 80]` array went near-white near the chart top.
      backgroundColor: '#ffffff',
      borderColor: '#ffffff',
      borderRadius: 10,
      borderWidth: 0,
      distance: 20,
      hideDelay: 50,
      // Body-level tooltip container: inside the chart it is trapped under the z-index:1
      // controls header (zoom/menu/chart-mode) by the highcharts-container's own stacking context.
      outside: true,
      // Off: without styledMode Highcharts fakes a shadow by stroking the box `width` times over,
      // which reads hard and flat however it is tuned. The box is given a real blur in CSS instead
      shadow: false,
      style: {
        color: '#98a1b6', // the quiet voice: unit words and the interval. Figures and answer: see the CSS.
        fontFamily: FONT_FAMILY,
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: '18px',
        // Spacing lives on .aytm-chart-tooltip so the CSS background covers it.
        padding: 0,
        textAlign: 'left'
      },
      useHTML: true
    },
    xAxis: {
      labels: {
        allowOverlap: false,
        autoRotation: 0,
        autoRotationLimit: 0,
        enabled: true,
        step: 1,
        style: {
          fontFamily: FONT_FAMILY,
          fontSize: '11px',
          fontWeight: 400,
          lineHeight: '10px',
          textOverflow: 'ellipsis',
          wordBreak: 'break-word'
        },
        y: 14
      },
      lineColor: '#999',
      title: {
        color: '#4896BC',
        style: {
          font: '11px Ubuntu'
        },
        text: ''
      }
    },
    yAxis: {
      // The scale is the chart's quietest layer: it names the ground the data stands on, so it reads
      // a step behind the columns rather than beside them. The design draws these rules as a hairline
      // a quarter of a pixel wide; a stroke that thin is at the mercy of zoom and pixel density and
      // survives export badly, so the line stays a whole pixel and carries the faintness in its
      // colour instead - what a quarter-pixel #cccccc settles to once the page shows through it.
      gridLineColor: '#f2f2f2',
      labels: {
        allowOverlap: false,
        autoRotation: 0,
        autoRotationLimit: 0,
        style: {
          color: '#98a1b6',
          fontFamily: FONT_FAMILY,
          fontSize: '11px',
          lineHeight: '10px',
          textOverflow: 'ellipsis',
          wordBreak: 'break-word'
        }
      },
      title: {
        style: {
          // colour belongs in `style` -- a `color` key on the title itself is not read by Highcharts
          color: '#98a1b6',
          font: '11px Ubuntu'
        },
        text: 'respondents'
      }
    }
  }

  return mergeDeep(base, overrides)
}
